import "@/lib/i18n";
import "@/index.css";
import { lazy, Suspense, useEffect, useRef, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { RealtimeProvider } from "@/contexts/RealtimeContext";
import { ConfirmProvider } from "@/contexts/ConfirmContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "sonner";

import AppLayout from "@/layouts/AppLayout";
import NewVersionBanner from "@/components/NewVersionBanner";
import OfflineBanner from "@/components/OfflineBanner";
const LoginPage = lazy(() => import("@/pages/Login"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPassword"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPassword"));
const ConfirmEmailPage = lazy(() => import("@/pages/ConfirmEmail"));
import ForcePasswordChange from "@/components/ForcePasswordChange";
const ActivatePage = lazy(() => import("@/pages/Activate"));
const OwnerDashboard = lazy(() => import("@/pages/OwnerDashboard"));
const HousesPage = lazy(() => import("@/pages/Houses"));
const HouseDetail = lazy(() => import("@/pages/HouseDetail"));
const TenantsPage = lazy(() => import("@/pages/Tenants"));
const OwnerTenantDetail = lazy(() => import("@/pages/OwnerTenantDetail"));
const TenantHome = lazy(() => import("@/pages/TenantHome"));
const TenantRoom = lazy(() => import("@/pages/TenantRoom"));
const TenantPayments = lazy(() => import("@/pages/TenantPayments"));
const OwnerPayments = lazy(() => import("@/pages/OwnerPayments"));
const OwnerUtilities = lazy(() => import("@/pages/OwnerUtilities"));
const BillDetail = lazy(() => import("@/pages/BillDetail"));
const BillAllocate = lazy(() => import("@/pages/BillAllocate"));
const OwnerDocuments = lazy(() => import("@/pages/OwnerDocuments"));
const TenantDocuments = lazy(() => import("@/pages/TenantDocuments"));
const OwnerFinance = lazy(() => import("@/pages/OwnerFinance"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const BizumSettings = lazy(() => import("@/pages/BizumSettings"));
const SettingsLayout = lazy(() => import("@/pages/settings/SettingsLayout"));
const SettingsBusiness = lazy(() => import("@/pages/settings/SettingsBusiness"));
const SettingsBankNew = lazy(() => import("@/pages/settings/SettingsBank"));
const SettingsAutomationNew = lazy(() => import("@/pages/settings/SettingsAutomation"));
const SettingsWhatsAppNew = lazy(() => import("@/pages/settings/SettingsWhatsApp"));
const SettingsEmail = lazy(() => import("@/pages/settings/SettingsEmail"));
const SettingsEmailTemplates = lazy(() => import("@/pages/settings/SettingsEmailTemplates"));
const SettingsNotificationTemplates = lazy(() => import("@/pages/settings/SettingsNotificationTemplates"));
const SettingsStripe = lazy(() => import("@/pages/settings/SettingsStripe"));
const SettingsHaier = lazy(() => import("@/pages/settings/SettingsHaier"));
const SettingsDaikin = lazy(() => import("@/pages/settings/SettingsDaikin"));
const SettingsApp = lazy(() => import("@/pages/settings/SettingsApp"));
import AppConfigBoot from "@/components/AppConfigBoot";
import { Loader2 } from "lucide-react";
import { useDarkClass } from "@/lib/theme";
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicy"));
const SettingsTemplates = lazy(() => import("@/pages/settings/SettingsTemplates"));
const SettingsSystem = lazy(() => import("@/pages/settings/SettingsSystem"));
const SettingsLocks = lazy(() => import("@/pages/settings/SettingsLocks"));
const SettingsNotifications = lazy(() => import("@/pages/settings/SettingsNotifications"));
const BizumSettingsPage = lazy(() => import("@/pages/BizumSettings"));
const TuyaSetup = lazy(() => import("@/pages/TuyaSetup"));
const ActivityLog = lazy(() => import("@/pages/ActivityLog"));
const ProfilePage = lazy(() => import("@/pages/Profile"));
const MorePage = lazy(() => import("@/pages/More"));
const SmartHub = lazy(() => import("@/pages/SmartHub"));
const NotificationsPage = lazy(() => import("@/pages/Notifications"));
const OnboardingWizard = lazy(() => import("@/pages/onboarding/OnboardingWizard"));
const ApplicationsPage = lazy(() => import("@/pages/Applications"));
const ApplicationDetail = lazy(() => import("@/pages/ApplicationDetail"));
const TenantApplication = lazy(() => import("@/pages/TenantApplication"));
const ContractsPage = lazy(() => import("@/pages/ContractsPage"));
const TenantContract = lazy(() => import("@/pages/TenantContract"));
const TenantCheckin = lazy(() => import("@/pages/TenantCheckin"));
const SettingsContracts = lazy(() => import("@/pages/settings/SettingsContracts"));
const SettingsPrivacy = lazy(() => import("@/pages/settings/SettingsPrivacy"));
const OwnerIncidents = lazy(() => import("@/pages/OwnerIncidents"));
const OwnerIncidentDetail = lazy(() => import("@/pages/OwnerIncidentDetail"));
const TenantIncidents = lazy(() => import("@/pages/TenantIncidents"));
const OwnerAnnouncements = lazy(() => import("@/pages/OwnerAnnouncements"));
const OwnerAnnouncementDetail = lazy(() => import("@/pages/OwnerAnnouncementDetail"));
const TenantAnnouncementDetail = lazy(() => import("@/pages/TenantAnnouncementDetail"));

function LoadingSplash() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-[color:var(--page-bg)] flex flex-col items-center justify-center gap-4">
      <img src="/icon-192.png" alt="" className="w-14 h-14 rounded-2xl animate-pulse" />
      <div className="text-stone-500 text-sm"><span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />{t("common.loading")}</span></div>
    </div>
  );
}

function ProtectedRoute({ children, role }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <LoadingSplash />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (user.must_change_password) return <ForcePasswordChange />;
  if (role && user.role !== role) {
    return <Navigate to={user.role === "owner" ? "/owner/dashboard" : "/tenant/home"} replace />;
  }
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Umbral de tiempo en segundo plano tras el cual, al volver a primer plano,
// tratamos la reanudación de la PWA como una apertura nueva.
const PWA_RESUME_THRESHOLD_MS = 15 * 60 * 1000; // 15 minutos

function isStandalonePwa() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator?.standalone === true
  );
}

const PWA_PUBLIC_PREFIXES = [
  "/login",
  "/forgot-password",
  "/reset-password",
  "/confirm-email",
  "/activate",
  "/onboarding",
  "/privacidad",
  "/pagos",
];

// Cuando la app se instala como PWA, el sistema operativo suele mantener el
// proceso en memoria y simplemente la trae a primer plano sin recargarla ni
// volver a navegar a "/", así que RootRedirect nunca se vuelve a ejecutar.
// Este componente fuerza la vuelta al Dashboard tanto en la apertura inicial
// (proceso nuevo) como al reanudar la app tras haber estado un buen rato en
// segundo plano.
function PwaLaunchRedirect() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  const didInitialRedirect = useRef(false);
  const hiddenAtRef = useRef(null);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  const goToDashboard = useCallback(() => {
    if (!user) return;
    const target = user.role === "owner" ? "/owner/dashboard" : "/tenant/home";
    const { pathname } = locationRef.current;
    const isPublic = PWA_PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));
    if (!isPublic && pathname !== target) {
      navigate(target, { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (didInitialRedirect.current) return;
    if (!user) return;
    didInitialRedirect.current = true;
    if (isStandalonePwa()) goToDashboard();
  }, [user, goToDashboard]);

  useEffect(() => {
    if (!isStandalonePwa()) return undefined;
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAtRef.current = Date.now();
      } else if (document.visibilityState === "visible") {
        const hiddenAt = hiddenAtRef.current;
        hiddenAtRef.current = null;
        if (hiddenAt && Date.now() - hiddenAt >= PWA_RESUME_THRESHOLD_MS) {
          goToDashboard();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [goToDashboard]);

  return null;
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return <LoadingSplash />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "owner" ? "/owner/dashboard" : "/tenant/home"} replace />;
}

function App() {
  const dark = useDarkClass();
  return (
    <ErrorBoundary>
    <AuthProvider>
      <RealtimeProvider>
      <ConfirmProvider>
      <AppConfigBoot />
      <Toaster position="top-center" theme={dark ? "dark" : "light"} />
      <BrowserRouter>
        <ScrollToTop />
        <PwaLaunchRedirect />
        <OfflineBanner />
        <NewVersionBanner />
        <Suspense fallback={<LoadingSplash />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/confirm-email/:token" element={<ConfirmEmailPage />} />
          <Route path="/activate/:token" element={<ActivatePage />} />
          <Route path="/onboarding/:token" element={<OnboardingWizard />} />
          <Route path="/privacidad" element={<PrivacyPolicyPage />} />
          <Route path="/pagos/success" element={<PaymentSuccess />} />
          <Route path="/pagos/cancel" element={<PaymentCancel />} />
          <Route path="/" element={<RootRedirect />} />

          <Route element={<ProtectedRoute role="owner"><AppLayout /></ProtectedRoute>}>
            <Route path="/owner/dashboard" element={<OwnerDashboard />} />
            <Route path="/owner/houses" element={<HousesPage />} />
            <Route path="/owner/houses/:houseId" element={<HouseDetail />} />
            <Route path="/owner/tenants" element={<TenantsPage />} />
            <Route path="/owner/tenants/:tenantId" element={<OwnerTenantDetail />} />
            <Route path="/owner/applications" element={<ApplicationsPage />} />
            <Route path="/owner/applications/:appId" element={<ApplicationDetail />} />
            <Route path="/owner/contracts" element={<ContractsPage />} />
            <Route path="/owner/payments" element={<OwnerPayments />} />
            <Route path="/owner/utilities" element={<OwnerUtilities />} />
            <Route path="/owner/utilities/bills/:billId" element={<BillDetail />} />
            <Route path="/owner/utilities/bills/:billId/allocate" element={<BillAllocate />} />
            <Route path="/owner/documents" element={<OwnerDocuments />} />
            <Route path="/owner/finance" element={<OwnerFinance />} />
            <Route path="/owner/incidents" element={<OwnerIncidents />} />
            <Route path="/owner/incidents/:incidentId" element={<OwnerIncidentDetail />} />
            <Route path="/owner/announcements" element={<OwnerAnnouncements />} />
            <Route path="/owner/announcements/:announcementId" element={<OwnerAnnouncementDetail />} />
            <Route path="/owner/smart" element={<SmartHub />} />
            {/* Redirects for legacy routes and existing notifications deep-links */}
            <Route path="/owner/accesses" element={<Navigate to="/owner/smart?tab=accesses" replace />} />
            <Route path="/owner/bizum" element={<Navigate to="/owner/settings/bizum" replace />} />
            <Route path="/owner/settings" element={<SettingsLayout />}>
              <Route index element={<div className="card-base text-stone-500">Pick a section</div>} />
              <Route path="business" element={<SettingsBusiness />} />
              <Route path="bizum" element={<BizumSettingsPage />} />
              <Route path="bank" element={<SettingsBankNew />} />
              <Route path="automation" element={<SettingsAutomationNew />} />
              <Route path="whatsapp" element={<SettingsWhatsAppNew />} />
              <Route path="email" element={<SettingsEmail />} />
              <Route path="email-templates" element={<SettingsEmailTemplates />} />
              <Route path="notification-templates" element={<SettingsNotificationTemplates />} />
              <Route path="stripe" element={<SettingsStripe />} />
              <Route path="app" element={<SettingsApp />} />
              <Route path="templates" element={<SettingsTemplates />} />
              <Route path="contracts" element={<SettingsContracts />} />
              <Route path="privacy" element={<SettingsPrivacy />} />
              <Route path="notifications" element={<SettingsNotifications />} />
              <Route path="locks" element={<SettingsLocks />} />
              <Route path="system" element={<SettingsSystem />} />
              <Route path="tuya" element={<TuyaSetup />} />
              <Route path="haier" element={<SettingsHaier />} />
              <Route path="daikin" element={<SettingsDaikin />} />
            </Route>
            <Route path="/owner/activity" element={<ActivityLog />} />
            <Route path="/owner/more" element={<MorePage />} />
          </Route>

          <Route element={<ProtectedRoute role="tenant"><AppLayout /></ProtectedRoute>}>
            <Route path="/tenant/home" element={<TenantHome />} />
            <Route path="/tenant/room" element={<TenantRoom />} />
            <Route path="/tenant/payments" element={<TenantPayments />} />
            <Route path="/tenant/documents" element={<TenantDocuments />} />
            <Route path="/tenant/incidents" element={<TenantIncidents />} />
            <Route path="/tenant/announcements/:announcementId" element={<TenantAnnouncementDetail />} />
            <Route path="/tenant/application" element={<TenantApplication />} />
            <Route path="/tenant/contract" element={<TenantContract />} />
            <Route path="/tenant/checkin" element={<TenantCheckin />} />
            <Route path="/tenant/more" element={<MorePage />} />
          </Route>

          <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
            <Route path="/profile" element={<ProfilePage />} />
            {/* Compartida por ambos roles: antes solo estaba dentro del grupo
                protegido de "owner", así que un inquilino que entraba aquí
                (p.ej. desde "Ver todas" en el desplegable de notificaciones)
                era rechazado por ProtectedRoute y devuelto a su inicio. */}
            <Route path="/notifications" element={<NotificationsPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      </ConfirmProvider>
      </RealtimeProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
